/**
 * CRTPipeline — Full-screen CRT post-processing effect.
 *
 * Single-pass fragment shader applied to the main camera via PostFXPipeline.
 * Effects: scanlines, barrel distortion, vignette, chromatic aberration, flicker.
 *
 * Params are read from SaveManager (crtScanlines, crtWarp, crtVignette)
 * and persisted across sessions.
 */

import Phaser from 'phaser';
import { SaveManager } from '@/systems/SaveManager';

const FRAG_SHADER = `
precision mediump float;

uniform sampler2D uMainSampler;
uniform vec2 uResolution;
uniform float uTime;
uniform float uScanIntensity;   // 0–1
uniform float uWarp;            // 0–1
uniform float uVignette;        // 0–1

varying vec2 outTexCoord;

// Barrel distortion
vec2 warp(vec2 uv, float k) {
  vec2 center = uv - 0.5;
  float r2 = dot(center, center);
  return uv + center * r2 * k;
}

void main() {
  // Barrel distortion (full strength of uniform)
  vec2 uv = warp(outTexCoord, uWarp);

  // Discard pixels outside [0,1] (warped corners)
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }

  // Chromatic aberration (scales with warp)
  float aberr = 0.002 * uWarp;
  float r = texture2D(uMainSampler, uv + vec2(aberr, 0.0)).r;
  float g = texture2D(uMainSampler, uv).g;
  float b = texture2D(uMainSampler, uv - vec2(aberr, 0.0)).b;
  vec3 color = vec3(r, g, b);

  // Scanlines — every other pixel row darkened
  float scanFreq = uResolution.y * 1.0;
  float scanline = sin(uv.y * scanFreq * 3.14159) * 0.5 + 0.5;
  color *= 1.0 - uScanIntensity * (1.0 - scanline);

  // Subtle phosphor glow between scanlines
  float glow = smoothstep(0.3, 0.7, scanline) * uScanIntensity * 0.08;
  color += glow;

  // Flicker
  color *= 1.0 - 0.015 * sin(uTime * 10.0);

  // Vignette — strong corner darkening
  vec2 vigUV = outTexCoord * (1.0 - outTexCoord);
  float vig = vigUV.x * vigUV.y * 15.0;
  vig = pow(clamp(vig, 0.0, 1.0), uVignette * 0.8);
  color *= vig;

  // Slight green tint (CRT phosphor feel)
  color *= vec3(0.97, 1.0, 0.97);

  gl_FragColor = vec4(color, 1.0);
}
`;

export class CRTPipeline extends Phaser.Renderer.WebGL.Pipelines.PostFXPipeline {
  private _time = 0;

  constructor(game: Phaser.Game) {
    super({
      game,
      fragShader: FRAG_SHADER,
    });
  }

  onPreRender(): void {
    this._time += 0.016;
    const s = SaveManager.getSettings();
    this.set2f('uResolution', this.renderer.width, this.renderer.height);
    this.set1f('uTime', this._time);
    this.set1f('uScanIntensity', s.crtScanlines ?? 0.7);
    this.set1f('uWarp', s.crtWarp ?? 0.6);
    this.set1f('uVignette', s.crtVignette ?? 0.5);
  }

  // ── Static helpers ──

  /** Register the pipeline in the renderer (idempotent) */
  static register(scene: Phaser.Scene): void {
    const renderer = scene.game.renderer;
    if (!(renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer)) return;
    if (renderer.pipelines.has('CRTPipeline')) return;
    renderer.pipelines.addPostPipeline('CRTPipeline', CRTPipeline);
  }

  /** Toggle the CRT effect on/off for a scene's main camera */
  static toggle(scene: Phaser.Scene, enabled: boolean): void {
    const cam = scene.cameras.main;
    if (!cam) return;

    if (enabled) {
      // getPostPipeline returns [] (truthy) when no match — check length too
      const existing = cam.getPostPipeline(CRTPipeline);
      const hasIt = Array.isArray(existing) ? existing.length > 0 : !!existing;
      if (!hasIt) {
        cam.setPostPipeline(CRTPipeline);
      }
    } else {
      cam.removePostPipeline('CRTPipeline');
    }
  }

  /** Check if CRT is currently active on a scene's main camera */
  static isActive(scene: Phaser.Scene): boolean {
    const cam = scene.cameras.main;
    if (!cam) return false;
    const p = cam.getPostPipeline(CRTPipeline);
    return Array.isArray(p) ? p.length > 0 : !!p;
  }
}
